// src/components/settings/shared/dialogs/KennelSideDialog.tsx

import React, { useState, useEffect } from 'react';
import { Box, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import DialogBase from '../../../common/modal/DialogBase';
import {
  DailogLabel,
  DialogLabelText,
  StyledTextField,
  StyledSwitch,
} from '../../../forms/shared/FormElements';
import FieldHelperText from '../../../forms/shared/FieldHelperText';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import { KennelSide, PetType } from '../../../../services/api';
import { DialogDivider } from '../SettingsElements';

interface KennelSideFormData {
  name: string;
  hasKennels: boolean;
  hasHeaters: boolean;
  allowedPetTypeIds: number[];
}

interface KennelSideDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: KennelSideFormData) => void;
  editing: KennelSide | null;
  petTypes?: PetType[];
}

const KennelSideDialog: React.FC<KennelSideDialogProps> = ({
  open,
  onClose,
  onSave,
  editing,
  petTypes = [],
}) => {
  const [formData, setFormData] = useState<KennelSideFormData>({
    name: '',
    hasKennels: true,
    hasHeaters: true,
    allowedPetTypeIds: [],
  });

  useEffect(() => {
    if (open) {
      setFormData(
        editing
          ? {
              name: editing.name,
              hasKennels: editing.hasKennels ?? true,
              hasHeaters: editing.hasHeaters ?? true,
              allowedPetTypeIds: editing.allowedPetTypeIds ?? [],
            }
          : {
              name: '',
              hasKennels: true,
              hasHeaters: true,
              allowedPetTypeIds: [],
            }
      );
    }
  }, [open, editing]);

  const canSave =
    formData.name.trim().length > 0 && formData.allowedPetTypeIds.length > 0;
  const isSystem = !!editing?.isSystem;

  const togglePetType = (petTypeId: number) => {
    const current = formData.allowedPetTypeIds;
    const next = current.includes(petTypeId)
      ? current.filter((id) => id !== petTypeId)
      : [...current, petTypeId].sort((a, b) => a - b);
    setFormData({ ...formData, allowedPetTypeIds: next });
  };

  const visiblePetTypes = petTypes.filter(
    (pt) => pt.active !== false || formData.allowedPetTypeIds.includes(pt.id)
  );

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Kennel Side' : 'Add Kennel Side'}
      headerIcon={editing ? <EditIcon /> : <AddIcon />}
      draggable
      footer={
        <>
          <ModalCancelButton
            onClick={onClose}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
          <ModalSaveButton
            onClick={() => onSave(formData)}
            disabled={!canSave}
            text={editing ? 'Save' : 'Save'}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={0.5} mt={0.2}>
        <DailogLabel>
          <DialogLabelText>Kennel Side Name:</DialogLabelText>
        </DailogLabel>
        <StyledTextField
          value={formData.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, name: e.target.value })
          }
          required
          fullWidth
          disabled={isSystem}
        />
        {isSystem && (
          <FieldHelperText helperText="Standard system item - name and switches cannot be changed. Pet types can." />
        )}
        <DialogDivider sx={{ mt: '.5rem', pt: '.5rem' }} />
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
            Kennels
          </Typography>
          <StyledSwitch
            checked={formData.hasKennels}
            disabled={isSystem}
            onChange={(e) =>
              setFormData({ ...formData, hasKennels: e.target.checked })
            }
          />
        </Box>
        <DialogDivider sx={{ mt: '.5rem', pt: '.5rem' }} />
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
            Heaters
          </Typography>
          <StyledSwitch
            checked={formData.hasHeaters}
            disabled={isSystem}
            onChange={(e) =>
              setFormData({ ...formData, hasHeaters: e.target.checked })
            }
          />
        </Box>
        <DialogDivider sx={{ mt: '.5rem', pt: '.5rem' }} />
        <Typography sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
          Pet types housed here
        </Typography>
        <Box display="flex" flexDirection="column">
          {visiblePetTypes.map((pt) => (
            <FormControlLabel
              key={pt.id}
              label={pt.type}
              control={
                <Checkbox
                  checked={formData.allowedPetTypeIds.includes(pt.id)}
                  onChange={() => togglePetType(pt.id)}
                  sx={{ '& svg': { fontSize: '2.8rem' } }}
                />
              }
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '1.8rem' } }}
            />
          ))}
        </Box>
        {formData.allowedPetTypeIds.length === 0 && (
          <FieldHelperText error helperText="Choose at least one pet type." />
        )}
      </Box>
    </DialogBase>
  );
};

export default KennelSideDialog;
